import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, requestFCMToken } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, onSnapshot, doc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import axios from 'axios';
import { releaseSlot } from '../utils/slotManager';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Scissors, Calendar, DollarSign, Clock, User, Camera, Upload, MapPin, 
  AlertTriangle, CreditCard, IndianRupee, Check, X, Bell, Phone, Timer,
  TrendingUp, Users, Star, BarChart3, Image, Settings, Power, Pencil, Gift, Sparkles
} from 'lucide-react';
import LocationPickerModal from '../components/LocationPickerModal';
import GooeyNav from '../components/GooeyNav';
import RotatingText from '../components/RotatingText';
import SalonManager from '../components/SalonManager';

// Use relative path for Firebase Functions
const API = '/api';
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_placeholder';

function SalonDashboard() {
  const navigate = useNavigate();
  const [salon, setSalon] = useState(null);
  const [gooeyTab, setGooeyTab] = useState(0);
  const [bookings, setBookings] = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [commissionData, setCommissionData] = useState(null);
  const [dashboardAnalytics, setDashboardAnalytics] = useState(null);
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [showGalleryDialog, setShowGalleryDialog] = useState(false);
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  const [showHoursDialog, setShowHoursDialog] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [bankDetails, setBankDetails] = useState({
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    upi_id: ''
  });
  const [hoursData, setHoursData] = useState({
    opening_time: '09:00',
    closing_time: '20:00'
  });
  const [loading, setLoading] = useState(true);
  const [payingCommission, setPayingCommission] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [autoCloseEnabled, setAutoCloseEnabled] = useState(true);
  const [salonTheme, setSalonTheme] = useState('open'); // 'open' | 'closed'
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('1_month');
  const [trialInfo, setTrialInfo] = useState(null);
  const [showEditProfileDialog, setShowEditProfileDialog] = useState(false);
  const [editProfileData, setEditProfileData] = useState({
    name: '',
    address: '',
    area: '',
    phone: '',
    secondary_phone: '',
    staff_count: 1,
    avg_service_time: 30,
    business_type: 'salon'
  });
  const [editProfileServices, setEditProfileServices] = useState([]);
  const [discountInfo, setDiscountInfo] = useState(null);

  const SUBSCRIPTION_PLANS = {
    '1_month':  { price: 999,  days: 30, name: '1 Month Plan' },
    '3_months': { price: 2999, days: 90, name: '3 Months Plan' }
  };

  // Wait for auth initialization
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthReady(true);
      if (user) {
        fetchSalonData();
      } else {
        const firebaseUid = localStorage.getItem('firebaseUid');
        const partnerStatus = localStorage.getItem('partnerStatus');
        if (firebaseUid && partnerStatus === 'active') {
          fetchSalonDataByUid(firebaseUid);
        } else {
          setLoading(false);
          navigate('/salon/login');
        }
      }
    });
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling for new bookings every 30 seconds
  useEffect(() => {
    if (!salon?.salon_id) return;
    const interval = setInterval(() => {
      fetchPendingBookings();
      checkAutoClose();
    }, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salon?.salon_id]);

  // REAL-TIME polling for bookings every 10 seconds
  useEffect(() => {
    if (!salon?.salon_id) return;

    let prevPendingIds = new Set();
    let isFirstLoad = true;

    const fetchAndNotify = async () => {
      try {
        const response = await axios.get(`${API}/bookings/salon/${salon.salon_id}/pending`);
        const data = Array.isArray(response.data) ? response.data : [];
        
        // Check for new bookings
        if (!isFirstLoad) {
          data.forEach(booking => {
            if (!prevPendingIds.has(booking.booking_id)) {
              // New booking detected!
              toast.info(`🔔 New Booking from ${booking.customer_name || 'Customer'}`, {
                description: `${booking.service_name} at ${booking.slot_time}`,
                duration: 8000
              });
              // Play alert sound
              try {
                // Try system notification sound first
                if ('Notification' in window && Notification.permission === 'granted') {
                  new Notification('🔔 New Booking!', {
                    body: `${booking.customer_name} ne ${booking.service_name} book kiya`,
                    icon: '/logo192.png',
                    silent: false,
                    tag: `booking-${booking.booking_id}`,
                  });
                }
                // Fallback audio
                const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWK0fPTgjMGHm7A7+OZUA0PVqzn77BdGAg+ltryxm8gBi6Dye/glkgLEmG56+mjUxELTKXh8bllHAU2jdXzzH0pBSd+zPDajzsIGGS86Oihkg==');
                audio.volume = 0.8;
                audio.play().catch(() => {});
              } catch(e) {}
            }
          });
        }

        prevPendingIds = new Set(data.map(b => b.booking_id));
        isFirstLoad = false;
        setPendingBookings(data);
      } catch(e) {
        console.warn('Pending bookings fetch failed:', e.message);
      }

      // Also fetch all bookings
      try {
        const allResp = await axios.get(`${API}/bookings/salon/${salon.salon_id}`);
        const allData = Array.isArray(allResp.data) ? allResp.data : [];
        setBookings(allData);
      } catch(e) {}
    };

    fetchAndNotify();
    const interval = setInterval(fetchAndNotify, 10000);
    return () => clearInterval(interval);
  }, [salon?.salon_id]);

  const fetchPendingBookings = async () => {
    if (!salon?.salon_id) return;
    try {
      const response = await axios.get(`${API}/bookings/salon/${salon.salon_id}/pending`);
      const data = Array.isArray(response.data) ? response.data : [];
      setPendingBookings(data);
    } catch (err) {
      console.error('Error fetching pending bookings:', err);
      setPendingBookings([]);
    }
  };

  const checkAutoClose = async () => {
    if (!salon?.salon_id || !autoCloseEnabled) return;
    try {
      const response = await axios.post(`${API}/salon/${salon.salon_id}/check-auto-close`);
      if (response.data.auto_closed) {
        toast.info('Salon is now marked as FULLY BOOKED for today');
        fetchSalonData();
      }
    } catch (error) {
      console.error('Auto-close check error:', error);
    }
  };

  const fetchDashboardAnalytics = async (salonId) => {
    try {
      const response = await axios.get(`${API}/salon/${salonId}/dashboard-analytics`);
      setDashboardAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchSalonDataByUid = async (uid) => {
    try {
      const salonResponse = await axios.get(`${API}/salon/user/${uid}`);
      const salonData = salonResponse.data || {};
      setSalon(salonData);
      // Save FCM token for notifications
      if (salonData?.salon_id) {
        saveFCMToken(salonData.salon_id);
      }
      setIsOpen(salonData.current_status !== 'closed');
      setAutoCloseEnabled(salonData.auto_close_enabled !== false);
      setHoursData({
        opening_time: salonData.opening_time || '09:00',
        closing_time: salonData.closing_time || '20:00'
      });
      
      // Calculate trial info - handle both Firestore Timestamp and plain date
      const getExpiryDate = (data) => {
        // Try subscription fields
        const raw = data.subscription?.expires_at
          || data.subscription?.trial_end_date
          || data.trialEndDate
          || null;
        if (!raw) return null;
        // Firestore Timestamp from API comes as { _seconds, _nanoseconds }
        if (raw._seconds) return new Date(raw._seconds * 1000);
        if (raw.toDate) return raw.toDate();
        return new Date(raw);
      };

      const expiryDate = getExpiryDate(salonData);
      if (expiryDate) {
        const nowD = new Date();
        const daysLeft = Math.ceil((expiryDate - nowD) / (1000 * 60 * 60 * 24));
        const sub = salonData.subscription || {};
        setTrialInfo({
          isFreeTrial: sub.plan === 'free_trial' || !sub.plan,
          daysRemaining: Math.max(0, daysLeft),
          expiresAt: expiryDate.toISOString(),
          planName: sub.plan_name || 'Free Trial',
          isExpired: daysLeft <= 0,
        });
      }
      
      if (salonData.salon_id) {
        const bookingsResponse = await axios.get(`${API}/bookings/salon/${salonData.salon_id}`);
        const bookingsData = Array.isArray(bookingsResponse.data) ? bookingsResponse.data : [];
        setBookings(bookingsData);
        fetchDashboardAnalytics(salonData.salon_id);
      }
    } catch (error) {
      console.error('Error fetching salon data:', error);
      if (error.response?.status === 404) {
        navigate('/salon/register');
      }
    }
    setLoading(false);
  };

  // Save FCM token for push notifications
  // Get fresh Firebase auth token for API calls
  // Build auth headers — works for both Firebase Auth and partner session
  // Memoised: SalonManager depends on this in a useCallback/useEffect chain.
  // An unstable identity here caused the summary API to be re-fetched on
  // every render (infinite request loop).
  const getAuthHeaders = useCallback(async () => {
    const headers = {};
    try {
      const token = await auth.currentUser?.getIdToken(true);
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch (e) { /* no firebase session — use partner headers */ }

    const partnerUid = localStorage.getItem('firebaseUid');
    const salonId = salon?.salon_id || localStorage.getItem('salonId');
    if (partnerUid) headers['X-Partner-Uid'] = partnerUid;
    if (salonId) headers['X-Salon-Id'] = salonId;
    return headers;
  }, [salon?.salon_id]);

  const getAuthToken = async () => {
    try { return await auth.currentUser?.getIdToken(true) || ''; } catch(e) { return ''; }
  };

  const saveFCMToken = async (salonId) => {
    try {
      const token = await requestFCMToken();
      if (token && salonId) {
        await axios.post(`${API}/fcm/save-token`, {
          token,
          salon_id: salonId
        });
        console.log('FCM token saved for salon');
      }
    } catch(e) {
      console.warn('FCM token save failed:', e.message);
    }
  };

  // ── Handle FCM notification click ───────────────────────────
  useEffect(() => {
    const handleMsg = (event) => {
      if (event.data?.type === 'BOOKING_NOTIFICATION_CLICK') {
        const bookingId = event.data.bookingId;
        fetchPendingBookings();
        setTimeout(() => {
          const el = document.querySelector('[data-testid="pending-bookings-alert"]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          if (bookingId) {
            const bookEl = document.querySelector(`[data-testid="pending-booking-${bookingId}"]`);
            if (bookEl) {
              bookEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
              bookEl.style.boxShadow = '0 0 0 3px #dc2626';
              setTimeout(() => { bookEl.style.boxShadow = ''; }, 3000);
            }
          }
        }, 1500);
        toast.info('🔔 New booking received!', { duration: 4000 });
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleMsg);
    return () => navigator.serviceWorker?.removeEventListener('message', handleMsg);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSalonData = async () => {
    try {
      const user = auth.currentUser;
      // Prefer salonId (document id — always correct) over firebaseUid, since a
      // partner login may carry a firebase_uid that doesn't match the salon record.
      const uid = localStorage.getItem('salonId') || user?.uid || localStorage.getItem('firebaseUid');
      
      if (!uid) {
        console.error('No authenticated user');
        setLoading(false);
        return;
      }
      
      const salonResponse = await axios.get(`${API}/salon/user/${uid}`);
      const salonData = salonResponse.data || {};
      setSalon(salonData);
      setIsOpen(salonData.current_status !== 'closed');
      setAutoCloseEnabled(salonData.auto_close_enabled !== false);
      setHoursData({
        opening_time: salonData.opening_time || '09:00',
        closing_time: salonData.closing_time || '20:00'
      });
      
      // Calculate trial info - handle both Firestore Timestamp and plain date
      const getExpiryDate = (data) => {
        // Try subscription fields
        const raw = data.subscription?.expires_at
          || data.subscription?.trial_end_date
          || data.trialEndDate
          || null;
        if (!raw) return null;
        // Firestore Timestamp from API comes as { _seconds, _nanoseconds }
        if (raw._seconds) return new Date(raw._seconds * 1000);
        if (raw.toDate) return raw.toDate();
        return new Date(raw);
      };

      const expiryDate = getExpiryDate(salonData);
      if (expiryDate) {
        const nowD = new Date();
        const daysLeft = Math.ceil((expiryDate - nowD) / (1000 * 60 * 60 * 24));
        const sub = salonData.subscription || {};
        setTrialInfo({
          isFreeTrial: sub.plan === 'free_trial' || !sub.plan,
          daysRemaining: Math.max(0, daysLeft),
          expiresAt: expiryDate.toISOString(),
          planName: sub.plan_name || 'Free Trial',
          isExpired: daysLeft <= 0,
        });
      }
      
      if (salonData.salon_id) {
        const bookingsResponse = await axios.get(`${API}/bookings/salon/${salonData.salon_id}`);
        const bookingsData = Array.isArray(bookingsResponse.data) ? bookingsResponse.data : [];
        setBookings(bookingsData);
        
        // Fetch pending bookings
        try {
          const pendingResponse = await axios.get(`${API}/bookings/salon/${salonData.salon_id}/pending`);
          const pendingData = Array.isArray(pendingResponse.data) ? pendingResponse.data : [];
          setPendingBookings(pendingData);
        } catch (err) {
          setPendingBookings([]);
        }
        
        // Fetch commission data - keeping for backward compatibility
        try {
          const commissionResponse = await axios.get(`${API}/commission/salon/${salonData.salon_id}`);
          setCommissionData(commissionResponse.data || {});
        } catch (err) {
          setCommissionData({});
        }

        // Fetch dashboard analytics
        fetchDashboardAnalytics(salonData.salon_id);
        
        // Fetch discount eligibility
        fetchDiscountEligibility();
      }
    } catch (error) {
      console.error('Error fetching salon data:', error);
      if (error.response?.status === 404) {
        navigate('/salon/register');
      }
    }
    setLoading(false);
  };

  // Fetch discount eligibility for first 100 salons offer
  const fetchDiscountEligibility = async () => {
    try {
      const response = await axios.get(`${API}/salon/discount-eligibility`);
      setDiscountInfo(response.data);
    } catch (error) {
      console.error('Error fetching discount info:', error);
    }
  };

  // Open edit profile dialog with current salon data
  const openEditProfileDialog = () => {
    if (salon) {
      setEditProfileData({
        name: salon.salon_name || '',
        address: salon.address || '',
        area: salon.area || '',
        phone: salon.phone || '',
        secondary_phone: salon.secondary_phone || '',
        staff_count: salon.staff_count || 1,
        avg_service_time: salon.avg_service_time || 30,
        business_type: salon.business_type || 'salon'
      });
      setEditProfileServices(salon.services || []);
      setShowEditProfileDialog(true);
    }
  };

  // Handle profile update
  const handleUpdateProfile = async () => {
    if (!salon?.salon_id) return;
    
    setLoading(true);
    try {
      const updatePayload = {
        salonId: salon.salon_id,
        firebase_uid: localStorage.getItem('firebaseUid'),
        name: editProfileData.name,
        address: editProfileData.address,
        area: editProfileData.area,
        phone: editProfileData.phone,
        secondary_phone: editProfileData.secondary_phone,
        staff_count: editProfileData.staff_count,
        avg_service_time: editProfileData.avg_service_time,
        business_type: editProfileData.business_type,
        services: editProfileServices.map(s => ({
          id: s.id,
          name: s.name,
          duration: s.duration || 30,
          priceType: s.priceType || 'fixed',
          price: s.priceType === 'range' ? (parseFloat(s.minPrice) || 0) : (parseFloat(s.price) || 0),
          minPrice: s.priceType === 'range' ? (parseFloat(s.minPrice) || 0) : null,
          maxPrice: s.priceType === 'range' ? (parseFloat(s.maxPrice) || 0) : null,
          displayPrice: s.priceType === 'range'
            ? `₹${s.minPrice} - ₹${s.maxPrice}`
            : `₹${s.price}`,
        }))
      };
      
      await axios.put(`${API}/salon/update-profile`, updatePayload);
      toast.success('Profile updated successfully!');
      setShowEditProfileDialog(false);
      fetchSalonData();
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error(error.response?.data?.error || 'Failed to update profile');
    }
    setLoading(false);
  };

  // Add service to edit form
  const addServiceToEdit = () => {
    setEditProfileServices([...editProfileServices, { 
      id: Date.now().toString(), name: '', priceType: 'fixed', 
      price: 0, minPrice: 0, maxPrice: 0, duration: 30 
    }]);
  };

  // Remove service from edit form
  const removeServiceFromEdit = (index) => {
    setEditProfileServices(editProfileServices.filter((_, i) => i !== index));
  };

  // Update service in edit form
  const updateServiceInEdit = (index, field, value) => {
    const updated = [...editProfileServices];
    updated[index] = { ...updated[index], [field]: value };
    setEditProfileServices(updated);
  };

  // Helper: find Firestore doc ID for a booking (by MongoDB booking_id or Firestore bookingId)
  const findFirestoreBookingRef = async (bookingId) => {
    // First try direct Firestore ID
    const directRef = doc(db, 'bookings', bookingId);
    try {
      const { getDoc } = await import('firebase/firestore');
      const snap = await getDoc(directRef);
      if (snap.exists()) return directRef;
    } catch(e) {}
    
    // Search by bookingId field or by mongoId field
    try {
      const { getDocs, query: fsQuery, where: fsWhere } = await import('firebase/firestore');
      const q1 = fsQuery(collection(db, 'bookings'), fsWhere('bookingId', '==', bookingId));
      const snap1 = await getDocs(q1);
      if (!snap1.empty) return doc(db, 'bookings', snap1.docs[0].id);
      
      const q2 = fsQuery(collection(db, 'bookings'), fsWhere('mongoId', '==', bookingId));
      const snap2 = await getDocs(q2);
      if (!snap2.empty) return doc(db, 'bookings', snap2.docs[0].id);
    } catch(e) {}
    
    return null;
  };

  const handleApproveBooking = async (bookingId) => {
    // ── Optimistic UI: remove from pending immediately ──────
    const prevPending = pendingBookings;
    setPendingBookings(prev => prev.filter(b =>
      (b.booking_id || b.bookingId || b.id) !== bookingId
    ));
    toast.success('Booking Confirmed!', { duration: 2500 });

    try {
      const headers = await getAuthHeaders();
      await axios.post(`${API}/booking/${bookingId}/approve`, {}, { headers, timeout: 8000 });

      // Firestore sync — background, non-blocking
      findFirestoreBookingRef(bookingId)
        .then(ref => ref && updateDoc(ref, { status: 'confirmed', confirmedAt: serverTimestamp() }))
        .catch(() => {});

      // Refresh in background
      fetchSalonData();
      fetchPendingBookings();
    } catch (error) {
      console.error('Approve error:', error);
      setPendingBookings(prevPending); // rollback
      toast.error(error.response?.data?.error || 'Failed to approve — please try again');
    }
  };

  const handleRejectBooking = async (bookingId, reason = 'Salon unavailable') => {
    const booking = [...(pendingBookings || []), ...(bookings || [])]
      .find(b => (b.booking_id || b.bookingId || b.id) === bookingId);

    // ── Optimistic UI ───────────────────────────────────────
    const prevPending = pendingBookings;
    setPendingBookings(prev => prev.filter(b =>
      (b.booking_id || b.bookingId || b.id) !== bookingId
    ));
    toast.success('Booking Rejected', { duration: 2500 });

    try {
      const headers = await getAuthHeaders();
      await axios.post(`${API}/booking/${bookingId}/reject?reason=${encodeURIComponent(reason)}`, {}, { headers, timeout: 8000 });

      // Background tasks — don't block the UI
      findFirestoreBookingRef(bookingId)
        .then(ref => ref && updateDoc(ref, {
          status: 'rejected', rejectionReason: reason, rejectedAt: serverTimestamp()
        }))
        .catch(() => {});

      if (booking) {
        releaseSlot(
          booking.salon_id || booking.salonId,
          booking.booking_date || booking.date,
          booking.slot_time || booking.slotTime
        ).catch(() => {});
      }

      fetchSalonData();
      fetchPendingBookings();
    } catch (error) {
      console.error('Reject error:', error);
      setPendingBookings(prevPending); // rollback
      toast.error(error.response?.data?.error || 'Failed to reject — please try again');
    }
  };

  const handleCompleteBooking = async (bookingId) => {
    try {
      // Update MongoDB via API
      await axios.patch(`${API}/admin/booking/${bookingId}/status?status=completed`);
      
      // Update Firestore for real-time sync
      try {
        const bookingRef = await findFirestoreBookingRef(bookingId);
        if (bookingRef) {
          await updateDoc(bookingRef, {
            status: 'completed',
            completedAt: serverTimestamp()
          });
        }
      } catch(fsError) {
        console.warn('Firestore update failed (non-critical):', fsError.message);
      }
      
      toast.success('Service Completed!');
      fetchSalonData();
    } catch (error) {
      console.error('Complete error:', error);
      toast.error('Failed to mark as completed');
    }
  };

  const toggleSalonStatus = async () => {
    try {
      const newStatus = !isOpen;
      await axios.patch(`${API}/salon/${salon.salon_id}/toggle-status`, {
        is_open: newStatus,
        auto_close_enabled: autoCloseEnabled
      });
      setIsOpen(newStatus);
      toast.success(`Salon is now ${newStatus ? 'OPEN' : 'CLOSED'}`);
      fetchSalonData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updateOperatingHours = async () => {
    try {
      await axios.patch(`${API}/salon/${salon.salon_id}/hours`, hoursData);
      toast.success('Operating hours updated!');
      setShowHoursDialog(false);
      fetchSalonData();
    } catch (error) {
      toast.error('Failed to update hours');
    }
  };

  const handlePayCommission = async () => {
    if (!salon || !commissionData?.current_month_ledger) {
      toast.error('No pending commission to pay');
      return;
    }
    
    setPayingCommission(true);
    try {
      const response = await axios.post(`${API}/commission/generate-payment-link/${salon.salon_id}`);
      
      const options = {
        key: response.data.razorpay_key || RAZORPAY_KEY,
        amount: response.data.amount * 100,
        currency: response.data.currency,
        name: 'BookYourSalons',
        description: `Commission Payment - ${response.data.billing_month}`,
        order_id: response.data.order_id,
        handler: async function (rzpResponse) {
          try {
            await axios.post(`${API}/commission/verify-payment`, {
              razorpay_payment_id: rzpResponse.razorpay_payment_id,
              razorpay_order_id: rzpResponse.razorpay_order_id,
              razorpay_signature: rzpResponse.razorpay_signature
            });
            toast.success('Commission payment successful!');
            fetchSalonData();
            setShowCommissionDialog(false);
          } catch (err) {
            toast.error('Payment verification failed');
          }
        },
        prefill: { contact: salon.phone },
        theme: { color: '#dc2626' }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error('Failed to initiate payment');
    }
    setPayingCommission(false);
  };

  const saveBankDetails = async () => {
    if (!bankDetails.account_holder_name || !bankDetails.bank_name || !bankDetails.account_number || !bankDetails.ifsc_code) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/salon/${salon.salon_id}/bank-details`, bankDetails);
      toast.success('Bank details saved successfully');
      setShowBankDialog(false);
      fetchSalonData();
    } catch (error) {
      toast.error('Failed to save bank details');
    }
    setLoading(false);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 8000000) {
        toast.error('File size must be less than 8MB');
        return;
      }
      // Accept any image type (jpg, png, webp, heic, gif, etc). The browser
      // reports HEIC as an empty type sometimes, so also allow files whose
      // name looks like an image even if the type is blank.
      const looksLikeImage = /\.(jpe?g|png|webp|heic|heif|gif|bmp)$/i.test(file.name || '');
      if (!(file.type.startsWith('image/') || looksLikeImage)) {
        toast.error('Please select an image file');
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleGallerySelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      toast.error('Maximum 5 photos at a time');
      return;
    }

    const validFiles = files.filter(f => {
      const looksLikeImage = /\.(jpe?g|png|webp|heic|heif|gif|bmp)$/i.test(f.name || '');
      return f.size <= 8000000 && (f.type.startsWith('image/') || looksLikeImage);
    });
    
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setGalleryPhotos(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const uploadGalleryPhotos = async () => {
    if (galleryPhotos.length === 0) {
      toast.error('Please select photos');
      return;
    }
    if (!salon?.salon_id) {
      toast.error('Salon not loaded yet. Please refresh and try again.');
      return;
    }

    setLoading(true);
    try {
      // Compress every photo first
      const compressed = [];
      for (const dataUrl of galleryPhotos) {
        compressed.push(await compressDataUrl(dataUrl, 900, 0.7));
      }

      const headers = await getAuthHeaders();

      // Send in small batches (3 at a time) so a large selection never
      // exceeds the server's size limit — this is why uploads sometimes failed.
      const BATCH_SIZE = 3;
      const batches = [];
      for (let i = 0; i < compressed.length; i += BATCH_SIZE) {
        batches.push(compressed.slice(i, i + BATCH_SIZE));
      }

      let uploaded = 0;
      for (const batch of batches) {
        let lastErr;
        for (let i = 0; i < 3; i++) {
          try {
            await axios.post(
              `${API}/salon/${salon.salon_id}/gallery`,
              { salon_id: salon.salon_id, photos: batch },
              { headers, timeout: 45000 }
            );
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
            const s = e?.response?.status;
            if (s && s >= 400 && s < 500) break;
            await new Promise((r) => setTimeout(r, 1200 * (i + 1)));
          }
        }
        if (lastErr) throw lastErr;
        uploaded += batch.length;
      }

      toast.success(`Gallery updated! ${uploaded} photo${uploaded > 1 ? 's' : ''} added`);
      setShowGalleryDialog(false);
      setGalleryPhotos([]);
      fetchSalonData();
    } catch (error) {
      console.error('Gallery upload failed:', error);
      const msg =
        error?.response?.data?.error || error?.message || 'Failed to upload photos';
      toast.error('Upload failed', { description: msg, duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  // Gallery variant — same guarantee: shrinks until it fits, never rejects
  // on a valid image. Smaller target since several photos go in one request.
  const compressDataUrl = (dataUrl, startWidth = 800) =>
    new Promise((resolve, reject) => {
      const img = new window.Image(); // window.* — lucide exports an `Image` icon
      img.onerror = () => reject(new Error('Invalid image'));
      img.onload = () => {
        const TARGET = 130 * 1024; // ~5 photos stay comfortably under 1MB

        const render = (maxW, q) => {
          let w = img.width;
          let h = img.height;
          if (w > maxW) {
            h = Math.round((h * maxW) / w);
            w = maxW;
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          return canvas.toDataURL('image/jpeg', q);
        };

        for (const w of [startWidth, 640, 480, 360, 280]) {
          for (const q of [0.7, 0.55, 0.4, 0.3]) {
            const out = render(w, q);
            if (out.length <= TARGET) {
              resolve(out);
              return;
            }
          }
        }
        resolve(render(200, 0.3)); // always resolves
      };
      img.src = dataUrl;
    });

  // Compress + resize so the payload always fits Firestore's 1MB doc limit.
  // Designed to NEVER fail on a real photo: it keeps shrinking dimensions and
  // quality until the result fits. Only a genuinely non-image file can reject.
  const compressImage = (file, startWidth = 1200) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Could not read the file'));
      reader.onload = () => {
        const img = new window.Image(); // window.* — lucide exports an `Image` icon
        img.onerror = () => reject(new Error('This file is not a valid image'));
        img.onload = () => {
          const TARGET = 650 * 1024; // safe margin under Firestore's 1MB limit

          const render = (maxW, q) => {
            let w = img.width;
            let h = img.height;
            if (w > maxW) {
              h = Math.round((h * maxW) / w);
              w = maxW;
            }
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            // White backdrop so PNGs with transparency don't turn black as JPEG
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            return canvas.toDataURL('image/jpeg', q);
          };

          // Try progressively smaller widths / lower quality until it fits.
          const widths = [startWidth, 1000, 800, 640, 480, 360];
          const qualities = [0.75, 0.6, 0.45, 0.35];

          for (const w of widths) {
            for (const q of qualities) {
              const out = render(w, q);
              if (out.length <= TARGET) {
                resolve(out);
                return;
              }
            }
          }

          // Absolute fallback — tiny thumbnail always fits
          resolve(render(240, 0.3));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });

  const uploadPhoto = async () => {
    if (!photoFile) {
      toast.error('Please select a photo');
      return;
    }

    if (!salon?.salon_id) {
      toast.error('Salon not loaded yet. Please refresh and try again.');
      return;
    }

    setLoading(true);
    try {
      const base64Photo = await compressImage(photoFile);
      const headers = await getAuthHeaders();

      // Retry on transient failures (cold start, flaky network) so the user
      // sees success rather than an error they'd just retry manually anyway.
      let lastErr;
      for (let i = 0; i < 3; i++) {
        try {
          await axios.post(
            `${API}/salon/${salon.salon_id}/upload-photo`,
            { salon_id: salon.salon_id, photo_url: base64Photo },
            { headers, timeout: 30000 }
          );
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          // Don't retry genuine rejections (bad data, too large, not found)
          const s = e?.response?.status;
          if (s && s >= 400 && s < 500) break;
          await new Promise((r) => setTimeout(r, 1200 * (i + 1)));
        }
      }
      if (lastErr) throw lastErr;

      toast.success('Photo uploaded successfully');
      setShowPhotoDialog(false);
      setPhotoFile(null);
      setPhotoPreview('');
      fetchSalonData();
    } catch (error) {
      // Previously the await lived inside reader.onloadend, so this catch
      // never fired and failures were completely silent.
      console.error('Photo upload failed:', error);
      const msg =
        error?.response?.data?.error ||
        error?.message ||
        'Failed to upload photo';
      toast.error('Upload failed', { description: msg, duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    auth.signOut();
    localStorage.clear();
    navigate('/');
  };

  const handleLocationUpdate = async (selectedLocation) => {
    try {
      await axios.patch(`${API}/salon/${salon.salon_id}/location`, {
        latitude: selectedLocation.lat,
        longitude: selectedLocation.lng
      });
      toast.success('Location updated successfully!');
      fetchSalonData();
    } catch (error) {
      toast.error('Failed to update location');
    }
  };

  // Safe array operations
  const safeBookings = Array.isArray(bookings) ? bookings : [];
  const todayBookings = safeBookings.filter(b => b && b.booking_date === new Date().toISOString().split('T')[0] && b.status === 'confirmed');
  const upcomingBookings = safeBookings.filter(b => b && new Date(b.booking_date) > new Date() && b.status === 'confirmed');
  const completedBookings = safeBookings.filter(b => b && b.status === 'completed');
  
  // 0% commission - salon partners receive 100% revenue
  const totalEarnings = safeBookings.filter(b => b && b.payment_status === 'paid').reduce((sum, b) => sum + (b.service_price || 0), 0);

  if (!salon) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pb-20"
      style={{
        background: salonTheme === 'open'
          ? 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 30%, #fdf4ff 70%, #f0fdf4 100%)'
          : 'linear-gradient(135deg, #0f0f1a 0%, #1a1025 30%, #0f172a 70%, #1a1a2e 100%)',
        transition: 'background 1.2s cubic-bezier(0.16,1,0.3,1)',
        minHeight: '100vh',
      }}
    >
      {/* Theme transition overlay */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: salonTheme === 'open'
          ? 'radial-gradient(ellipse at 20% 50%, rgba(167,139,250,0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(196,181,253,0.06) 0%, transparent 50%)'
          : 'radial-gradient(ellipse at 20% 50%, rgba(109,40,217,0.12) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(59,7,100,0.1) 0%, transparent 50%)',
        transition: 'background 1.2s ease',
      }} />
      {/* Premium Salon Nav — theme aware */}
      <nav
        className="sticky top-0 z-[100]"
        style={{
          background: salonTheme === 'open'
            ? 'rgba(255,255,255,0.88)'
            : 'rgba(15,15,26,0.92)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: salonTheme === 'open'
            ? '1px solid rgba(0,0,0,0.06)'
            : '1px solid rgba(255,255,255,0.08)',
          transition: 'background 1.2s ease, border-color 1.2s ease',
        }}
      >
        <div className="container mx-auto px-3 sm:px-4 py-2">
          {/* Row 1 — Salon name + Open/Close + Logout */}
          <div className="flex items-center justify-between gap-2 mb-2">
            {/* Salon Info */}
            <div className="flex items-center gap-2 min-w-0">
              <div className="bg-gradient-to-br from-purple-600 to-violet-500 p-1.5 rounded-lg shadow-sm flex-shrink-0">
                <Scissors className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm font-black truncate"
                  style={{ fontFamily:"'Syne',sans-serif", color: salonTheme==='open'?'#111827':'#f3f4f6' }}>
                  {salon.salon_name}
                </h1>
                <p className="text-xs truncate" style={{ color: salonTheme==='open'?'#6b7280':'#9ca3af' }}>
                  {salon.area}
                </p>
              </div>
            </div>

            {/* Open/Close + Logout */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={toggleSalonStatus}
                data-testid="salon-status-toggle"
                className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 font-bold text-xs transition-all"
                style={{
                  background: isOpen ? 'linear-gradient(135deg,#dcfce7,#bbf7d0)' : 'linear-gradient(135deg,#374151,#1f2937)',
                  border: isOpen ? '1.5px solid #86efac' : '1.5px solid #4b5563',
                  color: isOpen ? '#15803d' : '#9ca3af',
                  boxShadow: isOpen ? '0 2px 8px rgba(34,197,94,0.25)' : '0 2px 8px rgba(0,0,0,0.3)',
                }}
              >
                <div style={{
                  width:'7px', height:'7px', borderRadius:'50%',
                  background: isOpen?'#16a34a':'#6b7280',
                  boxShadow: isOpen?'0 0 5px #16a34a':'none',
                }} />
                {isOpen ? 'OPEN' : 'CLOSED'}
              </button>
              <button onClick={handleLogout}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all"
                style={{
                  background: salonTheme==='open'?'#f9fafb':'#1f2937',
                  border: salonTheme==='open'?'1px solid #e5e7eb':'1px solid #374151',
                  color: salonTheme==='open'?'#374151':'#9ca3af',
                }}>
                Logout
              </button>
            </div>
          </div>

          {/* Row 2 — Action buttons */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{scrollbarWidth:'none'}}>
            {[
              { label: 'Hours', icon: <Clock size={12}/>, action: ()=>setShowHoursDialog(true) },
              { label: 'Photo', icon: <Camera size={12}/>, action: ()=>setShowPhotoDialog(true) },
              { label: 'Gallery', icon: <Image size={12}/>, action: ()=>setShowGalleryDialog(true) },
              { label: 'Location', icon: <MapPin size={12}/>, action: ()=>setShowLocationDialog(true) },
              { label: 'Bank', icon: <CreditCard size={12}/>, action: ()=>setShowBankDialog(true) },
              { label: 'Edit', icon: <Settings size={12}/>, action: ()=>openEditProfileDialog() },
            ].map(({label, icon, action}) => (
              <button key={label} onClick={action}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold flex-shrink-0 transition-all active:scale-95"
                style={{
                  background: salonTheme==='open'
                    ? 'linear-gradient(135deg,#f5f3ff,#ede9fe)'
                    : 'linear-gradient(135deg,#1e1b4b,#2e1065)',
                  border: salonTheme==='open'?'1px solid #ddd6fe':'1px solid #4c1d95',
                  color: salonTheme==='open'?'#7c3aed':'#c4b5fd',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                }}
              >
                {icon}{label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Status Alerts */}
        {salon.status === 'pending' && (
          <Card className="mb-4 border-yellow-300 bg-yellow-50">
            <CardContent className="pt-4 sm:pt-6">
              <p className="text-center text-yellow-800 font-medium text-sm sm:text-base">
                Your salon is pending approval. You'll be able to receive bookings once approved by admin.
              </p>
            </CardContent>
          </Card>
        )}

        {salon.status === 'blocked' && (
          <Card className="mb-4 border-red-500 bg-red-50">
            <CardContent className="pt-4 sm:pt-6">
              <div className="flex items-center justify-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                <p className="font-medium text-sm sm:text-base">Your salon is blocked. Contact support.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {salon.current_status === 'fully_booked' && (
          <Card className="mb-4 border-orange-400 bg-orange-50">
            <CardContent className="pt-4">
              <p className="text-center text-orange-700 font-medium">
                Your salon is FULLY BOOKED for today! Great job!
              </p>
            </CardContent>
          </Card>
        )}

        {/* FREE TRIAL CARD */}
        {trialInfo && !trialInfo.isExpired && trialInfo.daysRemaining <= 3 && trialInfo.daysRemaining > 0 && (
          <div className="mb-4 animate-new-item">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-xl">
                  <AlertTriangle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">
                    ⚡ Subscription expires in {trialInfo.daysRemaining} day{trialInfo.daysRemaining > 1 ? 's' : ''}!
                  </p>
                  <p className="text-amber-100 text-xs">Renew now to avoid booking interruption.</p>
                </div>
              </div>
              <Button
                onClick={() => setShowUpgradeDialog(true)}
                size="sm"
                className="bg-white text-amber-600 hover:bg-amber-50 font-bold rounded-xl flex-shrink-0"
              >
                Renew
              </Button>
            </div>
          </div>
        )}

        {trialInfo && trialInfo.isFreeTrial && !trialInfo.isExpired && (
          <Card className="mb-4 border-2 border-green-400 bg-gradient-to-r from-green-50 to-emerald-50" data-testid="trial-card">
            <CardContent className="pt-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 p-3 rounded-full">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-green-700">FREE TRIAL ACTIVE</h3>
                    <p className="text-sm text-green-600">Enjoy unlimited bookings during your trial period</p>
                  </div>
                </div>
                <div className="text-center bg-white px-6 py-3 rounded-lg shadow-sm">
                  <p className="text-3xl font-bold text-green-700">{trialInfo.daysRemaining}</p>
                  <p className="text-xs text-gray-500">days remaining</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* TRIAL EXPIRING SOON WARNING */}
        {trialInfo && trialInfo.isFreeTrial && trialInfo.daysRemaining <= 7 && trialInfo.daysRemaining > 0 && (
          <Card className="mb-4 border-2 border-orange-400 bg-orange-50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="w-5 h-5" />
                <p className="font-medium">Your free trial ends in {trialInfo.daysRemaining} days! Upgrade now to continue.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* TRIAL EXPIRED */}
        {trialInfo && trialInfo.isExpired && (
          <Card className="mb-4 border-2 border-red-400 bg-red-50">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-red-600 to-rose-600 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="bg-white/20 p-2 rounded-xl flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-white text-sm">Subscription Expired!</p>
                      <p className="text-red-100 text-xs mt-1">
                        ⚠️ Customers cannot book your salon right now.
                        Renew to start receiving bookings again.
                      </p>
                      <p className="text-red-200 text-xs mt-1">
                        सब्सक्रिप्शन खत्म — customers booking nahi kar sakte.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => setShowUpgradeDialog(true)}
                    size="sm"
                    className="bg-white text-red-600 hover:bg-red-50 font-bold flex-shrink-0 rounded-xl"
                  >
                    Renew Now →
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* SPECIAL OFFER CARD - 50% Discount Highlight */}
        {discountInfo?.eligible && (
          <Card className="mb-4 border-2 border-orange-400 bg-gradient-to-r from-orange-50 via-red-50 to-pink-50 overflow-hidden relative" data-testid="special-offer-card">
            <div className="absolute top-0 right-0 bg-gradient-to-l from-red-500 to-orange-500 text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
              <Sparkles className="w-3 h-3 inline mr-1" />
              LIMITED OFFER
            </div>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="bg-gradient-to-br from-orange-500 to-red-500 p-4 rounded-full">
                  <Gift className="w-10 h-10 text-white" />
                </div>
                <div className="text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                    <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                      🔥 50% OFF
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-gray-800">You're eligible for 50% discount!</p>
                  <p className="text-sm text-orange-600 font-medium">
                    ⏰ Only {discountInfo.slotsRemaining || 0} slots remaining - Act fast!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* UPGRADE PLAN CARD */}
        <Card className="mb-4 border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50" data-testid="upgrade-plan-card">
          <CardContent className="pt-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-purple-700">Upgrade Your Plan</h3>
                <p className="text-sm text-gray-600">Unlock unlimited bookings and premium visibility.</p>
                {discountInfo?.eligible && (
                  <p className="text-sm font-semibold text-green-600 mt-1">
                    🎉 50% OFF - Only {discountInfo.slotsRemaining} slots left!
                  </p>
                )}
              </div>
              <Button 
                onClick={() => {
                  fetchDiscountEligibility();
                  setShowUpgradeDialog(true);
                }}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="upgrade-plan-btn"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Upgrade Plan
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 mb-6">
          <Card data-testid="today-bookings-card">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Today</CardDescription>
              <CardTitle className="text-2xl sm:text-3xl">{todayBookings.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="upcoming-bookings-card">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Upcoming</CardDescription>
              <CardTitle className="text-2xl sm:text-3xl">{upcomingBookings.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="total-earnings-card">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Your Revenue</CardDescription>
              <CardTitle className="text-2xl sm:text-3xl">₹{totalEarnings}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="rating-card" className="bg-yellow-50">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs flex items-center gap-1">
                <Star className="w-3 h-3" />Rating
              </CardDescription>
              <CardTitle className="text-2xl sm:text-3xl text-yellow-700">{salon.rating || '4.0'}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* ── Salon Manager — revenue, settlements, expenses ── */}
        {salon?.salon_id && (
          <div className="mb-6" data-testid="salon-manager">
            <SalonManager
              salonId={salon.salon_id}
              getAuthHeaders={getAuthHeaders}
              onDataChange={fetchSalonData}
            />
          </div>
        )}

        {/* Analytics Section */}
        {dashboardAnalytics && (
          <Card className="mb-6" data-testid="analytics-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <BarChart3 className="w-5 h-5" />
                Analytics Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-gray-600">This Week</p>
                  <p className="text-xl font-bold text-blue-700">{dashboardAnalytics.week?.bookings || 0}</p>
                  <p className="text-xs text-gray-500">₹{dashboardAnalytics.week?.revenue || 0}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-gray-600">This Month</p>
                  <p className="text-xl font-bold text-green-700">{dashboardAnalytics.month?.bookings || 0}</p>
                  <p className="text-xs text-gray-500">₹{dashboardAnalytics.month?.revenue || 0}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-gray-600">Total Customers</p>
                  <p className="text-xl font-bold text-purple-700">{dashboardAnalytics.customers?.total || 0}</p>
                  <p className="text-xs text-gray-500">{dashboardAnalytics.customers?.repeat || 0} repeat</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg">
                  <p className="text-xs text-gray-600">Repeat Rate</p>
                  <p className="text-xl font-bold text-orange-700">{dashboardAnalytics.customers?.repeat_rate || 0}%</p>
                </div>
              </div>

              {/* Popular Services */}
              {dashboardAnalytics.popular_services?.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-sm mb-2">Popular Services</h4>
                  <div className="flex flex-wrap gap-2">
                    {dashboardAnalytics.popular_services.map((service, idx) => (
                      <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                        {service.name} ({service.count})
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Peak Hours */}
              {dashboardAnalytics.peak_hours?.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-sm mb-2">Peak Hours</h4>
                  <div className="flex flex-wrap gap-2">
                    {dashboardAnalytics.peak_hours.map((hour, idx) => (
                      <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                        {hour.hour} ({hour.count})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pending Bookings Alert */}
        {pendingBookings.length > 0 && (
          <div className="mb-6 animate-new-item" data-testid="pending-bookings-alert">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-xl">
                  <Bell className="w-5 h-5 text-white notification-badge" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-base">
                    {pendingBookings.length} New Booking Request{pendingBookings.length > 1 ? 's' : ''}
                  </h3>
                  <p className="text-orange-100 text-xs">Respond within 15 minutes to confirm</p>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              {pendingBookings.map((booking, idx) => {
                const expiresAt = new Date(booking.approval_expires_at);
                const now = new Date();
                const minutesLeft = Math.max(0, Math.floor((expiresAt - now) / 60000));
                const isUrgent = minutesLeft <= 5;
                
                return (
                  <div 
                    key={booking.booking_id} 
                    className="booking-card border-l-4 border-l-orange-400 animate-new-item"
                    style={{ animationDelay: `${idx * 0.08}s` }}
                    data-testid={`pending-booking-${booking.booking_id}`}
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 text-orange-600" />
                            </div>
                            <div>
                              <p className="font-bold text-sm">{booking.customer_name}</p>
                              <p className="text-xs text-gray-500">{booking.customer_phone}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="bg-gray-50 rounded-lg px-3 py-2">
                              <p className="text-gray-400">Service</p>
                              <p className="font-semibold text-gray-800 truncate">{booking.service_name}</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg px-3 py-2">
                              <p className="text-gray-400">Price</p>
                              <p className="font-bold text-green-700">₹{booking.service_price}</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg px-3 py-2">
                              <p className="text-gray-400">Date</p>
                              <p className="font-semibold text-gray-800">{booking.booking_date}</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg px-3 py-2">
                              <p className="text-gray-400">Time</p>
                              <p className="font-bold text-blue-700">{booking.slot_time}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                          isUrgent ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-amber-100 text-amber-700'
                        }`}>
                          <Timer className="w-3 h-3" />
                          {minutesLeft > 0 ? `${minutesLeft} min left` : 'Expiring soon'}
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-4 font-semibold"
                            onClick={() => handleApproveBooking(booking.booking_id)}
                          >
                            <Check className="w-3.5 h-3.5 mr-1" />
                            Accept
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl px-4 font-semibold"
                            onClick={() => handleRejectBooking(booking.booking_id)}
                          >
                            <X className="w-3.5 h-3.5 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Salon Info & Bookings */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Salon Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Salon Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {salon.photo_url && (
                  <img src={salon.photo_url} alt="Salon" className="w-full h-40 sm:h-48 object-cover rounded-lg" />
                )}
                
                {salon.photo_gallery?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Gallery ({salon.photo_gallery.length} photos)</p>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {salon.photo_gallery.map((photo, idx) => (
                        <img key={idx} src={photo.url} alt={`Gallery ${idx}`} className="w-20 h-20 object-cover rounded flex-shrink-0" />
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600 text-xs">Owner</p>
                    <p className="font-medium">{salon.owner_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 text-xs">Phone</p>
                    <p className="font-medium">{salon.phone}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 text-xs">Staff</p>
                    <p className="font-medium">{salon.staff_count} members</p>
                  </div>
                  <div>
                    <p className="text-gray-600 text-xs">Avg Time</p>
                    <p className="font-medium">{salon.avg_service_time} min</p>
                  </div>
                  <div>
                    <p className="text-gray-600 text-xs">Opens</p>
                    <p className="font-medium">{salon.opening_time || '09:00'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 text-xs">Closes</p>
                    <p className="font-medium">{salon.closing_time || '20:00'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">Services</p>
                  <div className="flex flex-wrap gap-2">
                    {salon.services?.map((service) => (
                      <span key={service.id} className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                        {service.name} - {service.displayPrice || (service.priceType === 'range' && service.minPrice ? `₹${service.minPrice} - ₹${service.maxPrice}` : `₹${service.price}`)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bookings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="today">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="today" className="text-xs sm:text-sm">Today</TabsTrigger>
                  <TabsTrigger value="upcoming" className="text-xs sm:text-sm">Upcoming</TabsTrigger>
                  <TabsTrigger value="all" className="text-xs sm:text-sm">All</TabsTrigger>
                </TabsList>
                
                <TabsContent value="today" className="space-y-2 mt-4 max-h-80 overflow-y-auto">
                  {todayBookings.length === 0 ? (
                    <p className="text-center text-gray-500 py-8 text-sm">No bookings for today</p>
                  ) : (
                    todayBookings.map((booking) => <BookingCard key={booking.booking_id} booking={booking} onComplete={handleCompleteBooking} />)
                  )}
                </TabsContent>

                <TabsContent value="upcoming" className="space-y-2 mt-4 max-h-80 overflow-y-auto">
                  {upcomingBookings.length === 0 ? (
                    <p className="text-center text-gray-500 py-8 text-sm">No upcoming bookings</p>
                  ) : (
                    upcomingBookings.map((booking) => <BookingCard key={booking.booking_id} booking={booking} onComplete={handleCompleteBooking} />)
                  )}
                </TabsContent>

                <TabsContent value="all" className="space-y-2 mt-4 max-h-80 overflow-y-auto">
                  {bookings.length === 0 ? (
                    <p className="text-center text-gray-500 py-8 text-sm">No bookings yet</p>
                  ) : (
                    bookings.slice(0, 20).map((booking) => <BookingCard key={booking.booking_id} booking={booking} onComplete={handleCompleteBooking} />)
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Hours Dialog */}
      <Dialog open={showHoursDialog} onOpenChange={setShowHoursDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Operating Hours</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Opening Time</label>
                <Input
                  type="time"
                  value={hoursData.opening_time}
                  onChange={(e) => setHoursData({ ...hoursData, opening_time: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Closing Time</label>
                <Input
                  type="time"
                  value={hoursData.closing_time}
                  onChange={(e) => setHoursData({ ...hoursData, closing_time: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-sm">Auto-close when fully booked</p>
                <p className="text-xs text-gray-500">Automatically mark as fully booked</p>
              </div>
              <Switch
                checked={autoCloseEnabled}
                onCheckedChange={setAutoCloseEnabled}
              />
            </div>
            <Button className="w-full" onClick={updateOperatingHours}>
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bank Details Dialog */}
      <Dialog open={showBankDialog} onOpenChange={setShowBankDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bank Account Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Account Holder Name *</label>
              <Input value={bankDetails.account_holder_name} onChange={(e) => setBankDetails({ ...bankDetails, account_holder_name: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Bank Name *</label>
              <Input value={bankDetails.bank_name} onChange={(e) => setBankDetails({ ...bankDetails, bank_name: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Account Number *</label>
              <Input value={bankDetails.account_number} onChange={(e) => setBankDetails({ ...bankDetails, account_number: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">IFSC Code *</label>
              <Input value={bankDetails.ifsc_code} onChange={(e) => setBankDetails({ ...bankDetails, ifsc_code: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">UPI ID (Optional)</label>
              <Input value={bankDetails.upi_id} onChange={(e) => setBankDetails({ ...bankDetails, upi_id: e.target.value })} />
            </div>
            <Button className="w-full" onClick={saveBankDetails} disabled={loading}>
              {loading ? 'Saving...' : 'Save Bank Details'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Upload Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Salon Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {photoPreview && (
              <img src={photoPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
            )}
            <Input type="file" accept="image/*" onChange={handlePhotoSelect} />
            <p className="text-xs text-gray-500">Max size: 5MB | Formats: JPG, JPEG, PNG</p>
            <Button className="w-full" onClick={uploadPhoto} disabled={loading || !photoFile}>
              <Upload className="w-4 h-4 mr-2" />
              {loading ? 'Uploading...' : 'Upload Photo'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gallery Upload Dialog */}
      <Dialog open={showGalleryDialog} onOpenChange={setShowGalleryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Photo Gallery</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {galleryPhotos.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {galleryPhotos.map((photo, idx) => (
                  <div key={idx} className="relative">
                    <img src={photo} alt={`Upload ${idx}`} className="w-20 h-20 object-cover rounded" />
                    <button
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1"
                      onClick={() => setGalleryPhotos(prev => prev.filter((_, i) => i !== idx))}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Input type="file" accept="image/*" multiple onChange={handleGallerySelect} />
            <p className="text-xs text-gray-500">Select up to 5 photos at a time</p>
            <Button className="w-full" onClick={uploadGalleryPhotos} disabled={loading || galleryPhotos.length === 0}>
              <Upload className="w-4 h-4 mr-2" />
              {loading ? 'Uploading...' : `Upload ${galleryPhotos.length} Photos`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Location Update Modal */}
      <LocationPickerModal
        isOpen={showLocationDialog}
        onClose={() => setShowLocationDialog(false)}
        onLocationSelect={handleLocationUpdate}
        initialLocation={salon?.location?.coordinates ? { lat: salon.location.coordinates[1], lng: salon.location.coordinates[0] } : null}
        title="Update Salon Location"
      />

      {/* Upgrade Plan Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Upgrade Your Plan
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* 50% Discount Banner for First 100 Salons */}
            {discountInfo?.eligible && (
              <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 rounded-lg" data-testid="discount-banner">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🎉</span>
                  <div>
                    <p className="font-bold text-green-700">50% OFF - Early Bird Offer!</p>
                    <p className="text-xs text-green-600">
                      You're one of the first {100 - discountInfo.slotsRemaining + 1} salons! Only {discountInfo.slotsRemaining} slots left.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm text-gray-600">
              Choose a subscription plan to continue receiving bookings after your trial ends.
            </p>
            
            {/* Plan Options */}
            <div className="space-y-3">
              <div 
                onClick={() => setSelectedPlan('1_month')}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedPlan === '1_month' 
                    ? 'border-purple-600 bg-purple-50' 
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">1 Month Plan</p>
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-bold text-purple-700">₹499</p>
                      <p className="text-sm text-gray-400 line-through">₹999</p>
                      <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5 rounded">50% OFF</span>
                    </div>
                  </div>
                  {selectedPlan === '1_month' && (
                    <Check className="w-6 h-6 text-purple-600" />
                  )}
                </div>
              </div>
              
              <div 
                onClick={() => setSelectedPlan('3_months')}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all relative ${
                  selectedPlan === '3_months' 
                    ? 'border-purple-600 bg-purple-50' 
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <span className="absolute -top-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded">BEST VALUE</span>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">3 Months Plan</p>
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-bold text-purple-700">₹1499</p>
                      <p className="text-sm text-gray-400 line-through">₹2999</p>
                      <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5 rounded">50% OFF</span>
                    </div>
                    <p className="text-xs text-green-600">Save ₹1500 vs monthly</p>
                  </div>
                  {selectedPlan === '3_months' && (
                    <Check className="w-6 h-6 text-purple-600" />
                  )}
                </div>
              </div>
            </div>

            {/* UPI Payment Section */}
            <div className="p-4 bg-blue-50 rounded-xl">
              <p className="font-semibold text-blue-800 mb-3 text-center">Payment via UPI</p>

              {/* QR Code */}
              <div className="flex justify-center mb-3">
                <div className="bg-white p-2 rounded-xl shadow-sm border border-blue-100">
                  <img
                    src="/upi-qr.jpg"
                    alt="UPI QR Code - Scan to Pay"
                    style={{ width: '180px', height: '180px', objectFit: 'contain', borderRadius: '8px' }}
                  />
                </div>
              </div>

              <p className="text-xs text-center text-blue-600 mb-1">Scan QR or pay to UPI ID:</p>
              <div className="bg-white border border-blue-200 rounded-lg px-3 py-2 text-center mb-1">
                <p className="font-mono text-blue-800 text-xs font-bold">BHARATPE2K0S0Z6Z6A58787@unitype</p>
              </div>
              <p className="text-xs text-center text-blue-600 mb-3">
                Pay ₹{Math.round(SUBSCRIPTION_PLANS[selectedPlan].price * 0.5)}
              </p>

              {/* UPI app button — only useful on mobile */}
              <Button
                type="button"
                className="w-full bg-blue-600 hover:bg-blue-700 rounded-xl"
                onClick={() => {
                  const amount = Math.round(SUBSCRIPTION_PLANS[selectedPlan].price * 0.5);
                  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

                  if (!isMobile) {
                    toast.info('Scan the QR code above', {
                      description: 'UPI apps only open on mobile. On desktop, scan the QR with your phone.',
                      duration: 7000,
                    });
                    return;
                  }

                  const upiLink = `upi://pay?pa=BHARATPE2K0S0Z6Z6A58787@unitype&pn=BookYourSalons&am=${amount}&cu=INR&tn=Subscription_${selectedPlan}`;
                  // Use anchor click so browser treats it as a user gesture
                  const a = document.createElement('a');
                  a.href = upiLink;
                  a.rel = 'noopener';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);

                  // If no UPI app handled it, guide the user
                  setTimeout(() => {
                    if (!document.hidden) {
                      toast.info('No UPI app opened', {
                        description: 'Scan the QR code above, or pay manually to BHARATPE2K0S0Z6Z6A58787@unitype',
                        duration: 8000,
                      });
                    }
                  }, 2000);
                }}
              >
                <IndianRupee className="w-4 h-4 mr-2" />
                Pay ₹{Math.round(SUBSCRIPTION_PLANS[selectedPlan].price * 0.5)} via UPI App
              </Button>

              {/* Copy UPI ID fallback */}
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText('BHARATPE2K0S0Z6Z6A58787@unitype');
                  toast.success('UPI ID copied!', { description: 'Paste it in any UPI app to pay' });
                }}
                className="w-full mt-2 text-xs text-blue-600 font-semibold py-2 rounded-lg border border-blue-200 hover:bg-blue-50"
              >
                📋 Copy UPI ID
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              After payment, your subscription will be activated within 24 hours. Contact support if you face any issues.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT PROFILE DIALOG - Enhanced UI */}
      <Dialog open={showEditProfileDialog} onOpenChange={setShowEditProfileDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Pencil className="w-5 h-5 text-purple-600" />
              </div>
              Edit Salon Profile
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Basic Info Card */}
            <Card className="border-2 border-purple-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-purple-700 flex items-center gap-2">
                  <Scissors className="w-4 h-4" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      <span className="flex items-center gap-1">Salon Name <span className="text-red-500">*</span></span>
                    </label>
                    <Input
                      data-testid="edit-salon-name"
                      value={editProfileData.name || ''}
                      onChange={(e) => setEditProfileData({...editProfileData, name: e.target.value})}
                      placeholder="Your salon name"
                      className="border-gray-300 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Business Type</label>
                    <select
                      data-testid="edit-business-type"
                      value={editProfileData.business_type || 'salon'}
                      onChange={(e) => setEditProfileData({...editProfileData, business_type: e.target.value})}
                      className="w-full h-10 px-3 rounded-md border border-gray-300 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                    >
                      <option value="salon">💇 Salon</option>
                      <option value="spa">🧖 Spa</option>
                      <option value="barbershop">💈 Barbershop</option>
                      <option value="beauty_parlour">💄 Beauty Parlour</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Location Card */}
            <Card className="border-2 border-blue-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-blue-700 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Location Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700">
                    <span className="flex items-center gap-1">Address <span className="text-red-500">*</span></span>
                  </label>
                  <Input
                    data-testid="edit-address"
                    value={editProfileData.address || ''}
                    onChange={(e) => setEditProfileData({...editProfileData, address: e.target.value})}
                    placeholder="Full address"
                    className="border-gray-300 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700">Area/Locality</label>
                  <Input
                    data-testid="edit-area"
                    value={editProfileData.area || ''}
                    onChange={(e) => setEditProfileData({...editProfileData, area: e.target.value})}
                    placeholder="Area name (e.g., Koramangala, Bandra)"
                    className="border-gray-300 focus:border-blue-500"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Contact Card */}
            <Card className="border-2 border-green-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-green-700 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Primary Phone</label>
                    <Input
                      data-testid="edit-phone"
                      value={editProfileData.phone || ''}
                      onChange={(e) => setEditProfileData({...editProfileData, phone: e.target.value})}
                      placeholder="+91XXXXXXXXXX"
                      className="border-gray-300 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Secondary Phone</label>
                    <Input
                      value={editProfileData.secondary_phone || ''}
                      onChange={(e) => setEditProfileData({...editProfileData, secondary_phone: e.target.value})}
                      placeholder="Optional"
                      className="border-gray-300 focus:border-green-500"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Operations Card */}
            <Card className="border-2 border-orange-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-orange-700 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Operations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Staff Count</label>
                    <Input
                      type="number"
                      min="1"
                      value={editProfileData.staff_count || 1}
                      onChange={(e) => setEditProfileData({...editProfileData, staff_count: parseInt(e.target.value) || 1})}
                      className="border-gray-300 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Avg Service Time (min)</label>
                    <Input
                      type="number"
                      min="10"
                      value={editProfileData.avg_service_time || 30}
                      onChange={(e) => setEditProfileData({...editProfileData, avg_service_time: parseInt(e.target.value) || 30})}
                      className="border-gray-300 focus:border-orange-500"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Services Card */}
            <Card className="border-2 border-indigo-100">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-indigo-700 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Services & Pricing
                  </CardTitle>
                  <Button type="button" size="sm" variant="outline" onClick={addServiceToEdit} className="text-indigo-600 border-indigo-300 hover:bg-indigo-50">
                    + Add Service
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Price type info banner */}
                <div style={{background:'#f0fdf4',border:'1px solid #86efac',borderRadius:'10px',padding:'8px 12px',marginBottom:'10px',fontSize:'0.72rem',color:'#166534'}}>
                  💡 <b>Fixed</b> — e.g. Haircut = ₹150 &nbsp;|&nbsp; <b>Range</b> — e.g. Color = ₹500 to ₹1500
                </div>
                <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                  {editProfileServices.map((service, index) => (
                    <div key={service.id || index} style={{background:'#fafafa',borderRadius:'14px',padding:'12px',border:'1px solid #f3f4f6'}}>
                      {/* Service Name */}
                      <Input
                        placeholder="Service name (e.g., Haircut, Facial)"
                        value={service.name || ''}
                        onChange={(e) => updateServiceInEdit(index, 'name', e.target.value)}
                        className="mb-2 border-gray-300"
                        style={{borderRadius:'10px'}}
                      />
                      {/* Price Type Toggle */}
                      <div style={{display:'flex',gap:'6px',marginBottom:'8px'}}>
                        <button type="button"
                          onClick={() => updateServiceInEdit(index, 'priceType', 'fixed')}
                          style={{flex:1,padding:'6px',borderRadius:'8px',border:'none',cursor:'pointer',fontSize:'0.7rem',fontWeight:700,
                            background: (!service.priceType || service.priceType==='fixed') ? 'linear-gradient(135deg,#dc2626,#e11d48)' : '#f3f4f6',
                            color: (!service.priceType || service.priceType==='fixed') ? 'white' : '#6b7280',
                          }}>
                          📌 Fixed
                        </button>
                        <button type="button"
                          onClick={() => updateServiceInEdit(index, 'priceType', 'range')}
                          style={{flex:1,padding:'6px',borderRadius:'8px',border:'none',cursor:'pointer',fontSize:'0.7rem',fontWeight:700,
                            background: service.priceType==='range' ? 'linear-gradient(135deg,#7c3aed,#6d28d9)' : '#f3f4f6',
                            color: service.priceType==='range' ? 'white' : '#6b7280',
                          }}>
                          〜 Range
                        </button>
                        <Button type="button" size="sm" variant="ghost"
                          onClick={() => removeServiceFromEdit(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          style={{borderRadius:'8px',flexShrink:0}}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      {/* Price Input */}
                      {(!service.priceType || service.priceType==='fixed') ? (
                        <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                          <span style={{color:'#6b7280',fontWeight:600,fontSize:'0.82rem'}}>₹</span>
                          <Input type="number" placeholder="Fixed price (e.g. 150)"
                            value={service.price || ''}
                            onChange={(e) => updateServiceInEdit(index, 'price', parseInt(e.target.value) || 0)}
                            className="border-gray-300" style={{borderRadius:'8px'}}
                          />
                        </div>
                      ) : (
                        <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                          <span style={{color:'#6b7280',fontWeight:600,fontSize:'0.78rem',flexShrink:0}}>₹</span>
                          <Input type="number" placeholder="Min (e.g. 500)"
                            value={service.minPrice || ''}
                            onChange={(e) => updateServiceInEdit(index, 'minPrice', parseInt(e.target.value) || 0)}
                            className="border-gray-300" style={{borderRadius:'8px'}}
                          />
                          <span style={{color:'#9ca3af',fontWeight:700,fontSize:'0.75rem',flexShrink:0}}>to</span>
                          <span style={{color:'#6b7280',fontWeight:600,fontSize:'0.78rem',flexShrink:0}}>₹</span>
                          <Input type="number" placeholder="Max (e.g. 1500)"
                            value={service.maxPrice || ''}
                            onChange={(e) => updateServiceInEdit(index, 'maxPrice', parseInt(e.target.value) || 0)}
                            className="border-gray-300" style={{borderRadius:'8px'}}
                          />
                        </div>
                      )}
                      {/* Live preview */}
                      {service.name && ((service.priceType==='range' ? (service.minPrice && service.maxPrice) : service.price)) && (
                        <div style={{marginTop:'6px',background:'white',borderRadius:'8px',padding:'5px 10px',fontSize:'0.7rem',color:'#374151',border:'1px solid #e5e7eb'}}>
                          ✅ <b>{service.name}</b> — {service.priceType==='range' ? `₹${service.minPrice} - ₹${service.maxPrice}` : `₹${service.price}`}
                        </div>
                      )}
                    </div>
                  ))}
                  {editProfileServices.length === 0 && (
                    <div className="text-center py-6 text-gray-500">
                      <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No services added yet</p>
                      <p className="text-xs text-gray-400">Click "Add Service" to add your offerings</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3 pt-6 border-t">
              <Button 
                variant="outline" 
                onClick={() => setShowEditProfileDialog(false)}
                className="flex-1 py-5"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateProfile}
                className="flex-1 py-5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold shadow-lg"
                disabled={loading || !editProfileData.name}
                data-testid="save-profile-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    Save Changes
                  </span>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookingCard({ booking, onComplete }) {
  // Color-coded status badges
  const getStatusStyles = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'confirmed':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'completed':
        return 'bg-gray-100 text-gray-700 border-gray-300';
      case 'cancelled':
      case 'rejected':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-blue-100 text-blue-700 border-blue-300';
    }
  };

  return (
    <Card 
      className="overflow-hidden shadow-sm hover:shadow-md transition-all border-l-4" 
      style={{ borderLeftColor: 
        booking.status === 'pending' ? '#facc15' : 
        booking.status === 'confirmed' ? '#22c55e' : 
        booking.status === 'completed' ? '#9ca3af' : '#ef4444' 
      }}
      data-testid={`booking-card-${booking.booking_id || booking.bookingId}`}
    >
      <CardContent className="p-4">
        <div className="flex justify-between items-start gap-3">
          <div className="flex-1 space-y-2">
            {/* Customer Info */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-sm">{booking.customerName || booking.customer_name || 'Customer'}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {booking.customerPhone || booking.customer_phone || 'N/A'}
                </p>
              </div>
            </div>

            {/* Service Info */}
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-sm font-medium text-gray-800">{booking.serviceName || booking.service_name}</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-gray-600 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {booking.date || booking.booking_date} at {booking.slotTime || booking.slot_time}
                </p>
                <p className="text-sm font-bold text-green-600">₹{booking.servicePrice || booking.service_price}</p>
              </div>
            </div>
          </div>

          {/* Status and Actions */}
          <div className="flex flex-col items-end gap-2">
            <span className={`text-xs font-medium px-3 py-1 rounded-full border ${getStatusStyles(booking.status)}`}>
              {booking.status?.toUpperCase() || 'PENDING'}
            </span>

            {/* Complete Button - Show only for confirmed bookings */}
            {booking.status === 'confirmed' && onComplete && (
              <Button 
                size="sm"
                onClick={() => onComplete(booking.booking_id || booking.bookingId)}
                className="bg-blue-600 hover:bg-blue-700 text-xs h-7"
                data-testid={`complete-booking-${booking.booking_id}`}
              >
                <Check className="w-3 h-3 mr-1" />
                Complete
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default SalonDashboard;
